import React from "react";

/** Minimal annotation shape - id, start, end, text are required for overlap and rendering. */
export interface BaseAnnotation {
  id: number;
  start: number;
  end: number;
  text: string;
}

/** Style returned by getStyleForAnnotation for rendering annotated spans. */
export interface AnnotationStyle {
  color: string;
  textColor: string;
  label: string;
  /** Optional custom title (e.g. includes comment). Falls back to label + "Click to edit" when not readonly. */
  title?: string;
}

/**
 * Check if a selection [start, end) overlaps any existing annotation.
 */
export function checkOverlap(
  annotations: Array<{ start: number; end: number }>,
  start: number,
  end: number
): boolean {
  return annotations.some(
    (ann) =>
      (start >= ann.start && start < ann.end) ||
      (end > ann.start && end <= ann.end) ||
      (start <= ann.start && end >= ann.end)
  );
}

/**
 * Compute character offsets { start, end } for a text selection.
 * @param textContent - Full plain text content
 * @param selectedText - The selected string (typically selection.toString().trim())
 * @param searchFromIndex - Optional start index for searching (e.g. range.startOffset)
 */
export function computeSelectionOffsets(
  textContent: string,
  selectedText: string,
  searchFromIndex: number = 0
): { start: number; end: number } | null {
  const trimmed = selectedText.trim();
  if (trimmed.length === 0) return null;

  const index = textContent.indexOf(trimmed, searchFromIndex);
  if (index === -1) return null;

  return {
    start: index,
    end: index + trimmed.length,
  };
}

/**
 * Returns plain text from HTML string.
 */
export function htmlToPlainText(html: string): string {
  if (!html || !html.trim()) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent ?? "";
}

/**
 * Check if content looks like HTML (contains tags).
 */
export function isHtmlContent(content: string): boolean {
  if (!content || !content.trim()) return false;
  return /<[a-z][\s\S]*>/i.test(content.trim());
}

/**
 * Compute character offsets from a DOM selection within a content root.
 */
export function computeSelectionOffsetsFromDom(
  contentRoot: Element,
  selection: Selection
): { start: number; end: number } | null {
  if (!selection || selection.rangeCount === 0) return null;
  const range = selection.getRangeAt(0);
  if (!contentRoot.contains(range.commonAncestorContainer)) return null;

  try {
    const startRange = document.createRange();
    startRange.selectNodeContents(contentRoot);
    startRange.setEnd(range.startContainer, range.startOffset);
    const a = startRange.toString().length;

    const endRange = document.createRange();
    endRange.selectNodeContents(contentRoot);
    endRange.setEnd(range.endContainer, range.endOffset);
    const b = endRange.toString().length;

    return { start: Math.min(a, b), end: Math.max(a, b) };
  } catch {
    return null;
  }
}

/**
 * Render HTML with annotated spans injected. Preserves HTML structure.
 */
export function renderAnnotatedHtml<T extends BaseAnnotation>(
  html: string,
  annotations: T[],
  getStyleForAnnotation: (annotation: T) => AnnotationStyle,
  onAnnotationClick: (id: number, event?: React.MouseEvent) => void,
  readonly: boolean
): React.ReactNode {
  if (!html || !html.trim()) return null;

  const div = document.createElement("div");
  div.innerHTML = html;

  const sorted = [...annotations].sort((a, b) => a.start - b.start);

  function walk(
    node: Node,
    pos: number
  ): { nodes: React.ReactNode[]; nextPos: number } {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? "";
      const len = text.length;
      const end = pos + len;
      const overlapping = sorted.filter(
        (a) => (a.start < end && a.end > pos)
      );
      if (overlapping.length === 0) {
        return { nodes: [text], nextPos: end };
      }
      const segments: React.ReactNode[] = [];
      let i = pos;
      let segStart = 0;
      for (const ann of overlapping) {
        const annStart = Math.max(ann.start, i);
        const annEnd = Math.min(ann.end, end);
        if (annStart > i) {
          segments.push(text.substring(segStart, segStart + (annStart - i)));
        }
        const style = getStyleForAnnotation(ann);
        const annText = text.substring(
          segStart + (annStart - i),
          segStart + (annEnd - i)
        );
        segments.push(
          <span
            key={`ann-${ann.id}`}
            className={`${style.color ?? ""} ${style.textColor ?? ""} px-0.5 py-0.5 box-decoration-clone cursor-pointer transition-all hover:opacity-80`}
            title={style.title ?? (readonly ? style.label : `${style.label}: Click to edit`)}
            onClick={
              !readonly ? (e) => onAnnotationClick(ann.id, e) : undefined
            }
          >
            {annText}
          </span>
        );
        segStart += annEnd - i;
        i = annEnd;
      }
      if (i < end) {
        segments.push(text.substring(segStart));
      }
      return { nodes: segments, nextPos: end };
    }

    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as Element;
      const tag = el.tagName.toLowerCase();
      if (tag === "script" || tag === "style") {
        return { nodes: [], nextPos: pos };
      }
      const childNodes: React.ReactNode[] = [];
      let p = pos;
      for (let i = 0; i < el.childNodes.length; i++) {
        const { nodes, nextPos } = walk(el.childNodes[i], p);
        childNodes.push(...(Array.isArray(nodes) ? nodes : [nodes]));
        p = nextPos;
      }
      const attribs: Record<string, string> = {};
      for (const a of Array.from(el.attributes)) {
        if (a.name === "class") attribs.className = a.value;
        else if (a.name !== "style") attribs[a.name] = a.value;
      }
      return {
        nodes: [
          React.createElement(
            tag,
            { key: `el-${pos}`, ...attribs },
            ...childNodes
          ),
        ],
        nextPos: p,
      };
    }
    return { nodes: [], nextPos: pos };
  }

  const { nodes, nextPos: _ } = walk(div, 0);
  return <>{nodes}</>;
}

/**
 * Render text with annotated spans. Uses getStyleForAnnotation to style each annotation.
 */
export function renderAnnotatedText<T extends BaseAnnotation>(
  text: string,
  annotations: T[],
  getStyleForAnnotation: (annotation: T) => AnnotationStyle,
  onAnnotationClick: (id: number, event?: React.MouseEvent) => void,
  readonly: boolean
): React.ReactNode {
  if (!text) return "";

  const result: React.ReactNode[] = [];
  let lastIndex = 0;

  const sorted = [...annotations].sort((a, b) => a.start - b.start);

  sorted.forEach((annotation, i) => {
    if (lastIndex < annotation.start) {
      result.push(
        <span key={`text-${i}`}>
          {text.substring(lastIndex, annotation.start)}
        </span>
      );
    }

    const style = getStyleForAnnotation(annotation);
    result.push(
      <span
        key={`annotation-${annotation.id}`}
        className={`${style.color ?? ""} ${style.textColor ?? ""} px-0.5 py-0.5 box-decoration-clone cursor-pointer transition-all hover:opacity-80`}
        title={style.title ?? (readonly ? style.label : `${style.label}: Click to edit`)}
        onClick={
          !readonly ? (e) => onAnnotationClick(annotation.id, e) : undefined
        }
      >
        {annotation.text}
      </span>
    );

    lastIndex = annotation.end;
  });

  if (lastIndex < text.length) {
    result.push(
      <span key="text-end">{text.substring(lastIndex)}</span>
    );
  }

  return result;
}
